document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const announce = (message) => {
  const region = document.querySelector('[data-live-region]');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 40);
};

const formatStoreMoney = (cents, currency) => {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || window.Shopify?.currency?.active
    }).format(Number(cents || 0) / 100);
  } catch {
    return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
  }
};

const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;'
})[character]);

const quantityControlIcon = (name) => `
  <svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
    ${name === 'plus'
      ? '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>'
      : '<path d="M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>'}
  </svg>
`;

class ThemeSelect extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) this.initialize();
    if (!this.initialized) return;
    document.addEventListener('click', this.handleOutsideClick);
    this.form?.addEventListener('reset', this.handleFormReset);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
    this.form?.removeEventListener('reset', this.handleFormReset);
  }

  initialize() {
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.initialized = true;
    this.form = this.select.form;
    this.handleOutsideClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.handleFormReset = () => window.setTimeout(() => this.sync(), 0);

    const sequence = ThemeSelect.sequence += 1;
    const baseId = this.select.id || `ThemeSelect-${sequence}`;
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.id = `${baseId}-Trigger-${sequence}`;
    this.trigger.className = 'theme-select__trigger';
    this.trigger.setAttribute('aria-haspopup', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');

    this.value = document.createElement('span');
    this.value.className = 'theme-select__value';
    const chevron = document.createElement('span');
    chevron.className = 'theme-select__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    this.trigger.append(this.value, chevron);

    this.menu = document.createElement('div');
    this.menu.id = `${baseId}-Menu-${sequence}`;
    this.menu.className = 'theme-select__menu';
    this.menu.hidden = true;
    this.trigger.setAttribute('aria-controls', this.menu.id);
    this.optionButtons = [];
    this.buildOptions();

    const label = this.select.id
      ? [...document.querySelectorAll('label[for]')].find((item) => item.htmlFor === this.select.id)
      : null;
    if (label) label.htmlFor = this.trigger.id;
    if (this.select.getAttribute('aria-label')) this.trigger.setAttribute('aria-label', this.select.getAttribute('aria-label'));
    if (this.select.getAttribute('aria-describedby')) this.trigger.setAttribute('aria-describedby', this.select.getAttribute('aria-describedby'));

    this.select.hidden = true;
    this.select.setAttribute('aria-hidden', 'true');
    this.select.tabIndex = -1;
    this.append(this.trigger, this.menu);

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (event) => this.handleTriggerKeydown(event));
    this.menu.addEventListener('keydown', (event) => this.handleMenuKeydown(event));
    this.select.addEventListener('change', () => this.sync());
    this.sync();
  }

  buildOptions() {
    const addOption = (option, groupDisabled = false) => {
      const index = [...this.select.options].indexOf(option);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'theme-select__option';
      button.dataset.optionIndex = String(index);
      button.textContent = option.textContent.trim();
      button.disabled = option.disabled || groupDisabled;
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.select.selectedIndex = index;
        this.sync();
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.close(true);
      });
      this.menu.append(button);
      this.optionButtons.push(button);
    };

    [...this.select.children].forEach((item) => {
      if (item.tagName === 'OPTGROUP') {
        const heading = document.createElement('div');
        heading.className = 'theme-select__group';
        heading.textContent = item.label;
        this.menu.append(heading);
        [...item.children].forEach((option) => addOption(option, item.disabled));
      } else if (item.tagName === 'OPTION') {
        addOption(item);
      }
    });
  }

  sync() {
    const selected = this.select.options[this.select.selectedIndex];
    this.value.textContent = selected?.textContent.trim() || 'Select';
    this.trigger.disabled = this.select.disabled;
    this.optionButtons.forEach((button) => {
      const isSelected = Number(button.dataset.optionIndex) === this.select.selectedIndex;
      if (isSelected) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  toggle() {
    if (this.hasAttribute('open')) this.close();
    else this.open();
  }

  open(focusLast = false) {
    if (this.trigger.disabled) return;
    document.querySelectorAll('theme-select[open]').forEach((item) => {
      if (item !== this) item.close();
    });
    this.removeAttribute('drop-up');
    this.setAttribute('open', '');
    this.menu.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const triggerBounds = this.trigger.getBoundingClientRect();
    const menuHeight = Math.min(this.menu.scrollHeight, 352);
    const spaceBelow = window.innerHeight - triggerBounds.bottom - 16;
    const spaceAbove = triggerBounds.top - 16;
    if (spaceBelow < Math.min(menuHeight, 200) && spaceAbove > spaceBelow) this.setAttribute('drop-up', '');
    if (focusLast !== false) {
      const enabled = this.optionButtons.filter((button) => !button.disabled);
      const selected = enabled.find((button) => button.hasAttribute('aria-current'));
      (focusLast === true ? enabled.at(-1) : selected || enabled[0])?.focus();
    }
  }

  close(returnFocus = false) {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.removeAttribute('drop-up');
    this.menu.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.trigger.focus();
  }

  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open(event.key === 'ArrowUp' ? true : 'selected');
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  handleMenuKeydown(event) {
    const enabled = this.optionButtons.filter((button) => !button.disabled);
    const current = enabled.indexOf(document.activeElement);
    let next = null;
    if (event.key === 'ArrowDown') next = enabled[Math.min(enabled.length - 1, current + 1)];
    if (event.key === 'ArrowUp') next = enabled[Math.max(0, current - 1)];
    if (event.key === 'Home') next = enabled[0];
    if (event.key === 'End') next = enabled.at(-1);
    if (next) {
      event.preventDefault();
      next.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    } else if (event.key === 'Tab') {
      this.close();
    }
  }
}

ThemeSelect.sequence = 0;

const enhanceThemeSelects = (root = document) => {
  const selects = [...root.querySelectorAll('select[data-theme-select]:not([data-theme-select-enhanced])')];
  selects.forEach((select) => {
    const parent = select.parentNode;
    if (!parent) return;
    const next = select.nextSibling;
    const wrapper = document.createElement('theme-select');
    wrapper.className = 'theme-select';
    select.setAttribute('data-theme-select-enhanced', 'true');
    wrapper.append(select);
    parent.insertBefore(wrapper, next);
  });
};

const ixCartMotion = {
  active: null,
  capture(form, button) {
    const scope = form.closest('[data-product-card], [data-quick-shop-product], .main-product') || form.closest('section');
    const picture = scope?.querySelector('.product-gallery__item.is-active img, .product-card__media img:not(.product-card__rollover), .quick-shop-product__image img') || scope?.querySelector('img');
    const imageRect = picture?.getBoundingClientRect();
    const rect = imageRect && imageRect.bottom > 0 && imageRect.top < innerHeight && imageRect.width > 0 ? imageRect : button?.getBoundingClientRect();
    if (!rect || !picture?.currentSrc) return null;
    return {src: picture.currentSrc, x: Math.max(32, Math.min(innerWidth - 32, rect.left + rect.width / 2)), y: Math.max(32, Math.min(innerHeight - 32, rect.top + rect.height / 2)), size: Math.max(72, Math.min(132, rect.width))};
  },
  async fly(source) {
    this.active?.cancel();
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || document.hidden || !Element.prototype.animate) return;
    const target = document.querySelector('[data-ix-cart-float]');
    if (!target || !source || document.querySelector('[data-cart-dialog][open]')) return;
    const rect = target.getBoundingClientRect();
    const endX = rect.left + rect.width / 2, endY = rect.top + rect.height / 2;
    const bendX = source.x + (endX - source.x) * .45;
    const bendY = Math.max(12, Math.min(source.y, endY) - Math.min(170, Math.abs(endX - source.x) * .3 + 65));
    const image = document.createElement('img');
    image.className = 'ix-cart-flight';
    image.src = source.src;
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.style.setProperty('--ix-flight-size', `${source.size}px`);
    document.body.append(image);
    const frames = Array.from({length:41}, (_, index) => {
      const t = index / 40, p = 1 - (1 - t) ** 1.6;
      const x = (1-p)**2 * source.x + 2*(1-p)*p*bendX + p*p*endX;
      const y = (1-p)**2 * source.y + 2*(1-p)*p*bendY + p*p*endY;
      const scale = 1 - p * .88;
      return {offset:t, transform:`translate(${x - source.size/2}px,${y - source.size/2}px) scale(${scale}) rotate(${Math.sin(p*Math.PI)*-10}deg)`, opacity:p < .9 ? 1 : (1-p)*10};
    });
    const animation = image.animate(frames, {duration:720, fill:'forwards'});
    this.active = animation;
    const cancel = () => animation.cancel();
    window.addEventListener('resize',cancel,{once:true});
    document.addEventListener('visibilitychange',cancel,{once:true});
    let landed = false;
    try { await animation.finished; landed = true; } catch {} finally {
      image.remove();
      window.removeEventListener('resize',cancel);
      document.removeEventListener('visibilitychange',cancel);
      if (this.active === animation) this.active = null;
    }
    if (landed) {
      const bounce = target.animate([{transform:'scale(1)'},{transform:'translateY(-5px) scale(1.12)',offset:.4},{transform:'scale(1)'}],{duration:260,easing:'ease-out'});
      try { await bounce.finished; } catch {}
    }
  }
};

class SellingTools extends HTMLElement {
  connectedCallback() {
    this.rootUrl = this.dataset.rootUrl || '/';
    this.currency = this.dataset.currency || window.Shopify?.currency?.active;
    this.quickDialog = this.querySelector('[data-quick-shop-dialog]');
    this.cartDialog = this.querySelector('[data-cart-dialog]');
    this.compareDialog = this.querySelector('[data-compare-dialog]');
    this.productCache = new Map();
    this.quantityTimer = null;

    this.querySelectorAll('[data-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
    [this.quickDialog, this.cartDialog, this.compareDialog].forEach((dialog) => {
      dialog?.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });

    document.addEventListener('click', (event) => this.handleDocumentClick(event));
    document.addEventListener('submit', (event) => this.handleProductSubmit(event));
    document.addEventListener('cart:changed', (event) => this.handleCartAdded(event));
    document.addEventListener('cart:updated', (event) => this.updateFloatingCount(event.detail));
    this.cartDialog?.addEventListener('close', () => {
      this.cartMotionTicket = (this.cartMotionTicket || 0) + 1;
      this.querySelector('[data-ix-cart-float]')?.setAttribute('aria-expanded', 'false');
    });
    document.addEventListener('compare:changed', () => this.syncCompareDock());
    this.querySelector('[data-compare-clear]')?.addEventListener('click', () => this.clearComparison());
    this.querySelector('[data-compare-open]')?.addEventListener('click', () => this.openComparison());
    this.syncCompareDock();
    this.bindCartControls();
  }

  async getProduct(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);
    const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load this product');
    const product = await response.json();
    this.productCache.set(handle, product);
    return product;
  }

  handleDocumentClick(event) {
    const quickTrigger = event.target.closest('[data-quick-shop-trigger]');
    if (quickTrigger) {
      event.preventDefault();
      this.openQuickShop(quickTrigger.dataset.productHandle);
      return;
    }

    const cartTrigger = event.target.closest('[data-cart-trigger]');
    if (cartTrigger) {
      event.preventDefault();
      this.cartMotionTicket = (this.cartMotionTicket || 0) + 1;
      ixCartMotion.active?.cancel();
      this.refreshCart(true);
      return;
    }

  }

  async handleProductSubmit(event) {
    const form = event.target;
    if (!form.matches?.('.product-buy__form') || !event.submitter?.matches('[data-product-submit]')) return;
    event.preventDefault();
    const button = event.submitter;
    if (button.disabled) return;
    const cartSource = ixCartMotion.capture(form, button);
    button.disabled = true;
    try {
      const response = await fetch(`${this.rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add item');
      announce(`${data.product_title || 'Item'} added to cart`);
      if (form.closest('[data-quick-shop-product]')) this.quickDialog?.close();
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: {...data, ixCartSource: cartSource} }));
    } catch (error) {
      announce(error.message);
    } finally {
      button.disabled = false;
    }
  }

  async openQuickShop(handle) {
    if (!handle || !this.quickDialog) return;
    const content = this.querySelector('[data-quick-shop-content]');
    if (content) content.innerHTML = '<div class="selling-tools__loading" role="status">Loading</div>';
    if (!this.quickDialog.open) this.quickDialog.showModal();

    try {
      const url = `${this.rootUrl}products/${encodeURIComponent(handle)}?section_id=quick-shop-product`;
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Unable to load this product');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const product = parsed.querySelector('[data-quick-shop-product]');
      if (!content || !product) throw new Error('Unable to load this product');
      content.replaceChildren(product);
      enhanceThemeSelects(content);
      this.querySelector('[data-quick-shop-title]').textContent = product.dataset.productTitle || 'Quick shop';
    } catch (error) {
      if (content) content.innerHTML = `<div class="selling-tools__empty"><p>${escapeHTML(error.message)}</p></div>`;
    }
  }

  updateFloatingCount(cart) {
    if (!cart || !Number.isFinite(cart.item_count)) return;
    this.querySelectorAll('[data-ix-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
  }

  openCart() {
    if (!this.cartDialog || this.cartDialog.open) return;
    this.cartDialog.showModal();
    this.querySelector('[data-ix-cart-float]')?.setAttribute('aria-expanded', 'true');
  }

  async handleCartAdded(event) {
    const ticket = this.cartMotionTicket = (this.cartMotionTicket || 0) + 1;
    const [cart] = await Promise.all([this.refreshCart(false), ixCartMotion.fly(event.detail?.ixCartSource).catch(() => {})]);
    if (ticket !== this.cartMotionTicket) return;
    if (cart) this.openCart();
    else window.location.assign(`${this.rootUrl}cart`);
  }

  async refreshCart(open = false) {
    try {
      const response = await fetch(`${this.rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to refresh cart');
      const cart = await response.json();
      this.renderCart(cart);
      if (open) this.openCart();
      return cart;
    } catch (error) {
      announce(error.message);
      if (open) window.location.assign(`${this.rootUrl}cart`);
    }
  }

  renderCart(cart) {
    this.updateFloatingCount(cart);
    const content = this.querySelector('[data-cart-content]');
    const count = this.querySelector('[data-cart-count]');
    const total = this.querySelector('[data-cart-total]');
    const footer = this.querySelector('[data-cart-footer]');
    if (count) count.textContent = `(${cart.item_count})`;
    if (total) total.textContent = formatStoreMoney(cart.total_price, cart.currency || this.currency);
    if (footer) footer.hidden = cart.item_count === 0;

    document.querySelectorAll('.market-header__cart strong').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });

    if (content) {
      if (!cart.items.length) {
        content.innerHTML = `
          <div class="selling-tools__empty">
            <h3>Your cart is empty</h3>
            <p>${escapeHTML(this.dataset.emptyMessage || 'Add products to begin your order.')}</p>
            <button class="button button--secondary" type="button" data-dialog-close>Continue shopping</button>
          </div>
        `;
      } else {
        content.innerHTML = `<div class="cart-drawer__items">${cart.items.map((item, index) => `
          <article class="cart-drawer__item" data-cart-line="${escapeHTML(item.key)}">
            <a class="cart-drawer__image" href="${escapeHTML(item.url)}">
              ${item.image ? `<img src="${escapeHTML(item.image)}${item.image.includes('?') ? '&' : '?'}width=176" alt="${escapeHTML(item.product_title)}" width="88" height="88">` : ''}
            </a>
            <div>
              <a class="cart-drawer__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
              ${(item.options_with_values || []).filter(option => option.value !== 'Default Title').map((option) => `<p>${escapeHTML(option.name)}: ${escapeHTML(option.value)}</p>`).join('')}
              ${(!item.variant_title || item.variant_title === 'Default Title') && /fabric-cleaner-500-ml|carnauba-wax-with-sio-500-ml/.test(item.handle || '') ? '<p>Size: 500 ml</p>' : ''}
              ${item.selling_plan_allocation?.selling_plan?.name ? `<p>${escapeHTML(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
              ${(item.line_level_discount_allocations || []).length ? `<ul class="cart-drawer__discounts" role="list">${item.line_level_discount_allocations.map((allocation) => `<li>${escapeHTML(allocation.discount_application?.title || 'Discount')} (-${formatStoreMoney(allocation.amount, cart.currency || this.currency)})</li>`).join('')}</ul>` : ''}
              <div class="cart-drawer__line-bottom">
                <quantity-input class="quantity-input quantity-input--compact">
                  <button type="button" name="minus" aria-label="Decrease quantity">${quantityControlIcon('minus')}</button>
                  <label for="CartDrawerAjaxQuantity-${index}"><span class="visually-hidden">Quantity</span><input id="CartDrawerAjaxQuantity-${index}" type="number" min="0" value="${item.quantity}" data-cart-quantity></label>
                  <button type="button" name="plus" aria-label="Increase quantity">${quantityControlIcon('plus')}</button>
                </quantity-input>
                <span><small>${formatStoreMoney(item.final_price, cart.currency || this.currency)} each</small><strong>${formatStoreMoney(item.final_line_price, cart.currency || this.currency)}</strong>${item.unit_price_measurement ? `<small>${formatStoreMoney(item.unit_price, cart.currency || this.currency)} / ${item.unit_price_measurement.reference_value !== 1 ? `${item.unit_price_measurement.reference_value} ` : ''}${escapeHTML(item.unit_price_measurement.reference_unit)}</small>` : ''}</span>
              </div>
              <button type="button" class="cart-drawer__remove" data-cart-remove>Remove</button>
            </div>
          </article>
        `).join('')}</div>`;
      }
    }

    this.refreshCompanion(cart);
    this.updateShipping(cart.total_price);
    this.bindCartControls();
  }

  bindCartControls() {
    this.querySelectorAll('[data-cart-quantity]').forEach((input) => {
      input.dataset.savedQuantity = input.value;
      input.onchange = () => {
        this.changeCartLine(input);
      };
    });
    this.querySelectorAll('[data-cart-remove]').forEach((button) => {
      button.onclick = () => {
        const line = button.closest('[data-cart-line]');
        this.changeCartLine(line?.querySelector('[data-cart-quantity]'), 0);
      };
    });
    this.querySelectorAll('[data-dialog-close]').forEach((button) => {
      button.onclick = () => button.closest('dialog')?.close();
    });
  }

  async refreshCompanion(cart) {
    const holder = this.querySelector('[data-ix-drawer-companion]');
    if (!holder) return;
    this.companionRequest?.abort();
    holder.replaceChildren();
    if (!cart.item_count) return;
    const controller = new AbortController();
    this.companionRequest = controller;
    try {
      const response = await fetch(`${this.rootUrl}cart?section_id=ix-cart-companion`, {signal:controller.signal});
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-ix-companion-section]');
      if (!controller.signal.aborted && content) holder.replaceChildren(content);
    } catch (error) { if (error.name !== 'AbortError') holder.replaceChildren(); }
  }

  async changeCartLine(input, quantity = Number(input?.value || 0)) {
    const line = input?.closest('[data-cart-line]');
    if (!line?.dataset.cartLine || this.cartBusy) return;
    const status = this.querySelector('[data-ix-drawer-status]');
    if (!Number.isInteger(quantity) || quantity < 0) {
      if(status) status.textContent = this.dataset.ixQuantityError;
      input.value = input.dataset.savedQuantity || 1;
      return;
    }
    this.cartBusy = true;
    this.companionRequest?.abort();
    const focusKey = line.dataset.cartLine;
    const controls = [...this.cartDialog.querySelectorAll('button:not([data-dialog-close]),input,select')];
    controls.forEach(control=>{control.dataset.wasDisabled=String(control.disabled);control.disabled=true;});
    this.cartDialog.setAttribute('aria-busy','true');
    if(status) status.textContent = this.dataset.ixBusy;
    try {
      const response = await fetch(`${this.rootUrl}cart/change.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: focusKey, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || this.dataset.ixError);
      this.renderCart(cart);
      if(status) status.textContent='';
      const row=[...this.querySelectorAll('[data-cart-line]')].find(n=>n.dataset.cartLine===focusKey);
      (row?.querySelector('[data-cart-quantity]') || this.cartDialog.querySelector('[data-dialog-close]'))?.focus();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      if(status) status.textContent=error.message || this.dataset.ixError;
      input.value=input.dataset.savedQuantity || 1;
      announce(error.message);
    } finally {
      controls.forEach(control=>{control.disabled=control.dataset.wasDisabled==='true';delete control.dataset.wasDisabled;});
      this.cartDialog.removeAttribute('aria-busy');
      this.cartBusy=false;
    }
  }

  updateShipping(total) {
    const threshold = Number(this.dataset.freeShippingThreshold || 0);
    const wrapper = this.querySelector('[data-cart-shipping]');
    const message = this.querySelector('[data-cart-shipping-message]');
    const meter = this.querySelector('[data-cart-shipping-meter]');
    if (!wrapper || threshold <= 0) return;
    wrapper.hidden = false;
    const remaining = Math.max(0, threshold - Number(total || 0));
    if (message) {
      message.textContent = remaining > 0
        ? (this.dataset.shippingRemaining || '').replace('[amount]', formatStoreMoney(remaining, this.currency))
        : this.dataset.shippingQualified || '';
    }
    if (meter) meter.style.width = `${Math.min(100, (Number(total || 0) / threshold) * 100)}%`;
  }

  readComparison() {
    try {
      const items = JSON.parse(localStorage.getItem('catalog-command-compare') || '[]');
      return Array.isArray(items) ? items.filter(Boolean).slice(0, 4) : [];
    } catch {
      return [];
    }
  }

  syncCompareDock() {
    const items = this.readComparison();
    const dock = this.querySelector('[data-compare-dock]');
    const count = this.querySelector('[data-compare-count]');
    if (dock) dock.hidden = items.length === 0;
    if (count) count.textContent = items.length;
    document.querySelectorAll('compare-toggle').forEach((toggle) => toggle.sync?.());
  }

  clearComparison() {
    localStorage.removeItem('catalog-command-compare');
    this.syncCompareDock();
    if (this.compareDialog?.open) this.openComparison();
    announce('Comparison cleared');
  }

  async openComparison() {
    if (!this.compareDialog) return;
    const handles = this.readComparison();
    const grid = this.querySelector('[data-compare-grid]');
    const empty = this.querySelector('[data-compare-empty]');
    if (!this.compareDialog.open) this.compareDialog.showModal();
    if (!handles.length) {
      if (grid) grid.replaceChildren();
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    if (grid) grid.innerHTML = '<div class="selling-tools__loading" role="status">Loading</div>';

    try {
      const products = await Promise.all(handles.map((handle) => this.getProduct(handle)));
      if (!grid) return;
      grid.innerHTML = products.map((product) => {
        const variant = product.variants.find((item) => item.available) || product.variants[0];
        const image = variant?.featured_image?.src || product.featured_image || product.images?.[0] || '';
        return `
          <article class="compare-center__product">
            <button type="button" class="compare-center__remove" data-compare-remove="${escapeHTML(product.handle)}" aria-label="Remove ${escapeHTML(product.title)}">×</button>
            <a href="${escapeHTML(product.url)}">${image ? `<img src="${escapeHTML(image)}&width=520" alt="${escapeHTML(product.title)}" width="520" height="520">` : ''}</a>
            <p>${escapeHTML(product.vendor || '')}</p>
            <h3><a href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a></h3>
            <strong>${formatStoreMoney(variant?.price, this.currency)}</strong>
            <dl>
              <div><dt>Availability</dt><dd>${variant?.available ? 'In stock' : 'Unavailable'}</dd></div>
              <div><dt>Options</dt><dd>${escapeHTML((product.options || []).map((option) => option.name).join(', ') || 'Standard')}</dd></div>
              <div><dt>Variants</dt><dd>${product.variants.length}</dd></div>
              <div><dt>Product type</dt><dd>${escapeHTML(product.type || '—')}</dd></div>
            </dl>
            <a class="button button--secondary" href="${escapeHTML(product.url)}">View product</a>
          </article>
        `;
      }).join('');
      grid.querySelectorAll('[data-compare-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          const next = this.readComparison().filter((handle) => handle !== button.dataset.compareRemove);
          localStorage.setItem('catalog-command-compare', JSON.stringify(next));
          this.syncCompareDock();
          this.openComparison();
        });
      });
    } catch (error) {
      if (grid) grid.innerHTML = `<div class="selling-tools__empty"><p>${escapeHTML(error.message)}</p></div>`;
    }
  }

}

class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.closeButton = this.querySelector('[data-menu-close]');
    this.closeButton?.addEventListener('click', () => this.close());
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  close() {
    if (!this.details) return;
    this.details.removeAttribute('open');
    this.details.querySelector('summary')?.focus();
  }
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.abortController = null;
    this.timer = null;
    this.input?.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 180);
    });
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  close() {
    this.abortController?.abort();
    this.results?.replaceChildren();
    this.input?.setAttribute('aria-expanded', 'false');
  }

  handleKeydown(event) {
    const options = [...this.querySelectorAll('[role="option"]')];
    if (event.key === 'Escape') {
      this.close();
      this.input?.focus();
      return;
    }
    if (!options.length || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = options.indexOf(document.activeElement);
    let nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    if (event.key === 'ArrowDown' && currentIndex >= 0) nextIndex = (currentIndex + 1) % options.length;
    if (event.key === 'ArrowUp') nextIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
    options[nextIndex].focus();
  }

  async search() {
    const query = this.input?.value.trim();
    if (!query || query.length < 2) {
      this.close();
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const limit = this.dataset.limit || '6';
    const resources = this.dataset.resources || 'product,collection,article,page';
    const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=${resources}&resources[limit]=${limit}&section_id=predictive-search`;

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error('Predictive search request failed');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('#shopify-section-predictive-search');
      if (this.results && content) {
        this.results.innerHTML = content.innerHTML;
        this.input?.setAttribute('aria-expanded', 'true');
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    }
  }
}

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', (event) => this.submit(event));
  }

  async submit(event) {
    event.preventDefault();
    if (!this.form || !this.button || this.button.disabled) return;
    const cartSource = ixCartMotion.capture(this.form, this.button);
    this.button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add item');
      announce(`${data.product_title || 'Item'} added to cart`);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: {...data, ixCartSource: cartSource} }));
      this.button.dataset.originalText ||= this.button.textContent;
      this.button.textContent = this.dataset.addedText || 'Added';
      window.setTimeout(() => {
        this.button.textContent = this.button.dataset.originalText;
      }, 1600);
    } catch (error) {
      announce(error.message);
    } finally {
      this.button.disabled = false;
    }
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => this.change(button.name === 'plus' ? 1 : -1));
    });
  }

  change(amount) {
    if (!this.input) return;
    const minimum = Number(this.input.min || 1);
    const maximum = Number(this.input.max || Infinity);
    const next = Math.min(maximum, Math.max(minimum, Number(this.input.value || minimum) + amount));
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

class GiftCardRecipient extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-recipient-toggle]');
    this.fields = this.querySelector('[data-recipient-fields]');
    this.inputs = [...this.querySelectorAll('[data-recipient-input]')];
    this.email = this.querySelector('[data-recipient-email]');
    this.form = this.closest('form');
    this.toggle?.addEventListener('change', () => this.sync());
    window.requestAnimationFrame(() => this.sync());
  }

  sync() {
    const enabled = Boolean(this.toggle?.checked);
    if (this.fields) this.fields.hidden = !enabled;
    this.inputs.forEach((input) => { input.disabled = !enabled; });
    if (this.email) this.email.required = enabled;
    const acceleratedCheckout = this.form?.querySelector('.shopify-payment-button');
    if (acceleratedCheckout) acceleratedCheckout.hidden = enabled;
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('[data-gallery-panel]')];
    this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.dialogImage = this.querySelector('[data-gallery-dialog-image]');
    this.dialogCounter = this.querySelector('[data-gallery-dialog-counter]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.imagePanels = this.panels.filter((panel) => panel.dataset.fullImage);
    this.currentIndex = Math.max(0, this.panels.findIndex((panel) => !panel.hidden));

    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialogImage?.addEventListener('load', () => this.dialogImage.classList.remove('is-loading'));
    this.selectMedia(this.panels[this.currentIndex]?.dataset.mediaId, { scrollThumbnail: false });
  }

  handleClick(event) {
    const thumbnail = event.target.closest('[data-gallery-thumbnail]');
    if (thumbnail) {
      this.selectMedia(thumbnail.dataset.mediaId);
      return;
    }
    if (event.target.closest('[data-gallery-previous]')) {
      this.step(-1);
      return;
    }
    if (event.target.closest('[data-gallery-next]')) {
      this.step(1);
      return;
    }
    const openButton = event.target.closest('[data-gallery-open]');
    if (openButton) {
      this.openDialog(openButton);
      return;
    }
    if (event.target.closest('[data-gallery-close]')) {
      this.dialog?.close();
      return;
    }
    if (event.target.closest('[data-gallery-dialog-previous]')) {
      this.stepDialog(-1);
      return;
    }
    if (event.target.closest('[data-gallery-dialog-next]')) this.stepDialog(1);
  }

  handleKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    if (this.dialog?.open) this.stepDialog(direction);
    else this.step(direction);
  }

  selectMedia(mediaId, options = {}) {
    const index = this.panels.findIndex((panel) => String(panel.dataset.mediaId) === String(mediaId));
    if (index < 0) return;
    this.currentIndex = index;
    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
      if (!selected) panel.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.thumbnails.forEach((thumbnail) => {
      const selected = String(thumbnail.dataset.mediaId) === String(mediaId);
      thumbnail.classList.toggle('is-active', selected);
      thumbnail.setAttribute('aria-current', selected ? 'true' : 'false');
      if (selected && options.scrollThumbnail !== false) {
        thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    });
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.panels.length}`;
  }

  step(amount) {
    if (this.panels.length < 2) return;
    const nextIndex = (this.currentIndex + amount + this.panels.length) % this.panels.length;
    this.selectMedia(this.panels[nextIndex].dataset.mediaId);
  }

  openDialog(trigger) {
    const panel = trigger.closest('[data-gallery-panel]');
    if (!panel?.dataset.fullImage || !this.dialog || !this.dialogImage) return;
    this.selectMedia(panel.dataset.mediaId, { scrollThumbnail: false });
    this.updateDialogImage();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  stepDialog(amount) {
    if (this.imagePanels.length < 2) return;
    const activePanel = this.panels[this.currentIndex];
    const imageIndex = Math.max(0, this.imagePanels.indexOf(activePanel));
    const nextImageIndex = (imageIndex + amount + this.imagePanels.length) % this.imagePanels.length;
    this.selectMedia(this.imagePanels[nextImageIndex].dataset.mediaId, { scrollThumbnail: false });
    this.updateDialogImage();
  }

  updateDialogImage() {
    const panel = this.panels[this.currentIndex];
    if (!panel?.dataset.fullImage || !this.dialogImage) return;
    this.dialogImage.classList.add('is-loading');
    this.dialogImage.src = panel.dataset.fullImage;
    this.dialogImage.alt = panel.dataset.imageAlt || '';
    if (this.dialogImage.complete) this.dialogImage.classList.remove('is-loading');
    const imageIndex = this.imagePanels.indexOf(panel);
    if (this.dialogCounter) this.dialogCounter.textContent = `${imageIndex + 1} / ${this.imagePanels.length}`;
    this.querySelectorAll('[data-gallery-dialog-previous], [data-gallery-dialog-next]').forEach((button) => {
      button.hidden = this.imagePanels.length < 2;
    });
  }

}

class ProductDescription extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-description-content]');
    this.button = this.querySelector('[data-description-toggle]');
    if (!this.content || !this.button) return;
    this.classList.add('is-collapsed');
    this.button.addEventListener('click', () => this.toggleDescription());
    window.requestAnimationFrame(() => {
      const isLong = this.content.scrollHeight > this.content.clientHeight + 4;
      this.button.hidden = !isLong;
      if (!isLong) this.classList.remove('is-collapsed');
    });
  }

  toggleDescription() {
    const willExpand = this.classList.contains('is-collapsed');
    if (willExpand) {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.classList.remove('is-collapsed');
      window.setTimeout(() => { this.content.style.maxHeight = 'none'; }, 300);
    } else {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
      this.content.getBoundingClientRect();
      this.classList.add('is-collapsed');
      this.content.style.maxHeight = '';
    }
    this.button.textContent = willExpand ? this.button.dataset.showLess : this.button.dataset.showMore;
    this.button.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
  }
}

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.form;
    this.productData = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', () => this.update());
    this.querySelectorAll('[data-option-value]').forEach((button) => {
      button.addEventListener('click', () => {
        const select = document.getElementById(button.dataset.selectId);
        if (!select) return;
        select.value = button.dataset.optionValue;
        button.parentElement?.querySelectorAll('[data-option-value]').forEach((item) => {
          item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
        });
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active;
    if (!currency) return `${(cents / 100).toFixed(2)}`;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  update() {
    const values = [...this.querySelectorAll('select')].map((select) => select.value);
    const variant = this.productData.find((item) => item.options.every((option, index) => option === values[index]));
    const form = document.getElementById(this.formId);
    const idInput = form?.querySelector('[name="id"]');
    if (!variant || !idInput) return;
    idInput.value = variant.id;
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
    const root = this.closest('section') || document;
    root.querySelectorAll('[data-product-submit]').forEach((button) => {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.addToCart : button.dataset.soldOut;
    });
    root.querySelectorAll('[data-product-sku]').forEach((node) => {
      node.textContent = variant.sku || 'Not assigned';
    });
    root.querySelectorAll('[data-product-price]').forEach((container) => {
      container.replaceChildren();
      const price = document.createElement('div');
      price.className = `price${variant.compare_at_price > variant.price ? ' price--sale' : ''}`;
      const current = document.createElement('span');
      current.textContent = this.formatMoney(variant.price);
      price.append(current);
      if (variant.compare_at_price > variant.price) {
        const compare = document.createElement('span');
        compare.className = 'price__compare';
        compare.textContent = this.formatMoney(variant.compare_at_price);
        price.append(compare);
      }
      if (variant.unit_price_measurement && variant.unit_price != null) {
        const unit = document.createElement('small');
        unit.className = 'price__unit';
        const referenceValue = variant.unit_price_measurement.reference_value !== 1
          ? `${variant.unit_price_measurement.reference_value} `
          : '';
        unit.textContent = `${this.formatMoney(variant.unit_price)} / ${referenceValue}${variant.unit_price_measurement.reference_unit}`;
        price.append(unit);
      }
      container.append(price);
    });
    root.querySelectorAll('[data-product-inventory]').forEach((inventory) => {
      const dot = inventory.querySelector('.product-inventory__dot');
      const text = inventory.querySelector('span:last-child');
      dot?.classList.toggle('product-inventory__dot--unavailable', !variant.available);
      if (text) text.textContent = variant.available ? 'In stock and ready to order' : 'Currently unavailable';
    });
    if (root.classList?.contains('main-product')) {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', nextUrl);
    }
    if (variant.featured_media?.id) {
      const gallery = root.querySelector('product-gallery');
      if (gallery?.selectMedia) gallery.selectMedia(variant.featured_media.id);
      else root.querySelector(`[data-media-id="${variant.featured_media.id}"]`)?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
    this.updatePickup(root, variant.id);
  }

  async updatePickup(root, variantId) {
    const pickup = root.querySelector('[data-product-pickup]');
    const productUrl = root.dataset.productUrl;
    const sectionId = root.dataset.sectionId;
    if (!pickup || !productUrl || !sectionId) return;
    this.pickupRequest?.abort();
    this.pickupRequest = new AbortController();
    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { signal: this.pickupRequest.signal });
      if (!response.ok) return;
      const html = await response.text();
      const nextPickup = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-pickup]');
      if (nextPickup) pickup.innerHTML = nextPickup.innerHTML;
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Pickup availability could not be refreshed.');
    }
  }
}

class CompareToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.productHandle;
    this.button?.addEventListener('click', () => this.toggle());
    this.sync();
  }

  get items() {
    try {
      return JSON.parse(localStorage.getItem('catalog-command-compare') || '[]');
    } catch {
      return [];
    }
  }

  toggle() {
    const items = this.items;
    const next = items.includes(this.handle)
      ? items.filter((handle) => handle !== this.handle)
      : [...items.slice(-3), this.handle];
    localStorage.setItem('catalog-command-compare', JSON.stringify(next));
    this.sync();
    document.dispatchEvent(new CustomEvent('compare:changed'));
    announce(next.includes(this.handle) ? 'Product added to comparison' : 'Product removed from comparison');
  }

  sync() {
    const active = this.items.includes(this.handle);
    this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.classList.toggle('is-active', active);
  }
}

class ProductCardVariants extends HTMLElement {
  connectedCallback() {
    this.card = this.closest('[data-product-card]');
    this.querySelectorAll('[data-variant-id]').forEach((button) => {
      button.addEventListener('click', () => this.select(button));
    });
  }

  select(button) {
    this.querySelectorAll('[data-variant-id]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
    button.setAttribute('aria-pressed', 'true');
    const image = this.card?.querySelector('.product-card__media img:not(.product-card__rollover)');
    if (image && button.dataset.variantImage) {
      image.src = button.dataset.variantImage;
      image.removeAttribute('srcset');
    }
    const priceContainer = this.card?.querySelector('.price');
    const price = priceContainer?.querySelector(':scope > span:first-child');
    const variantPrice = Number(button.dataset.variantPrice || 0);
    const compareAtPrice = Number(button.dataset.variantCompareAtPrice || 0);
    if (price) price.textContent = formatStoreMoney(variantPrice, window.Shopify?.currency?.active);
    if (priceContainer) {
      priceContainer.classList.toggle('price--sale', compareAtPrice > variantPrice);
      let compare = priceContainer.querySelector('.price__compare');
      if (compareAtPrice > variantPrice) {
        if (!compare) {
          compare = document.createElement('span');
          compare.className = 'price__compare';
          priceContainer.append(compare);
        }
        compare.textContent = formatStoreMoney(compareAtPrice, window.Shopify?.currency?.active);
      } else {
        compare?.remove();
      }
      let unit = priceContainer.querySelector('.price__unit');
      const unitPrice = Number(button.dataset.variantUnitPrice || 0);
      const referenceUnit = button.dataset.unitReferenceUnit;
      if (unitPrice && referenceUnit) {
        if (!unit) {
          unit = document.createElement('small');
          unit.className = 'price__unit';
          priceContainer.append(unit);
        }
        const referenceValue = Number(button.dataset.unitReferenceValue || 1);
        unit.textContent = `${formatStoreMoney(unitPrice, window.Shopify?.currency?.active)} / ${referenceValue !== 1 ? `${referenceValue} ` : ''}${referenceUnit}`;
      } else {
        unit?.remove();
      }
    }
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.innerHTML.trim()) return;
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const recommendations = parsed.querySelector('product-recommendations');
        if (recommendations?.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
      })
      .catch(() => {});
  }
}

class CollectionLoader extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.loadingMode || 'pagination';
    if (this.mode === 'pagination') return;
    this.bindControls();
  }

  bindControls() {
    this.control = this.querySelector('[data-collection-loader]');
    this.link = this.control?.querySelector('[data-load-more]');
    this.sentinel = this.control?.querySelector('[data-load-sentinel]');
    if (!this.link) return;
    this.link.addEventListener('click', (event) => {
      event.preventDefault();
      this.loadNextPage();
    }, { once: true });
    this.observer?.disconnect();
    if (this.mode === 'infinite' && this.sentinel) {
      this.link.hidden = true;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      }, { rootMargin: '500px 0px' });
      this.observer.observe(this.sentinel);
    }
  }

  async loadNextPage() {
    if (this.loading || !this.link?.href) return;
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    this.observer?.disconnect();
    const nextUrl = this.link.href;
    const originalText = this.link.textContent;
    this.link.hidden = false;
    this.link.textContent = 'Loading…';
    this.link.setAttribute('aria-disabled', 'true');
    try {
      const response = await fetch(nextUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Unable to load more products.');
      const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const currentGrid = this.querySelector('.collection-results__grid');
      const nextGrid = nextDocument.querySelector('.collection-results__grid');
      if (!currentGrid || !nextGrid) throw new Error('Unable to load more products.');
      [...nextGrid.children].forEach((card) => currentGrid.append(card));
      const nextControl = nextDocument.querySelector('[data-collection-loader]');
      if (nextControl) {
        this.control.replaceWith(nextControl);
        this.bindControls();
      } else {
        this.control.remove();
        this.control = null;
        this.link = null;
        announce('All products loaded');
      }
    } catch (error) {
      this.link.textContent = originalText;
      this.link.hidden = false;
      this.link.removeAttribute('aria-disabled');
      this.link.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadNextPage();
      }, { once: true });
      announce(error.message);
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
    }
  }
}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-bundle-item]')];
    this.button = this.querySelector('[data-bundle-submit]');
    this.buttonLabel = this.querySelector('[data-bundle-label]');
    this.total = this.querySelector('[data-bundle-total]');
    this.count = this.querySelector('[data-bundle-count]');
    this.items.forEach((item) => item.addEventListener('change', () => this.update()));
    this.button?.addEventListener('click', () => this.submit());
    this.update();
  }

  get selectedItems() {
    return this.items.filter((item) => item.checked && !item.disabled && item.dataset.variantId);
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${this.dataset.currency || ''}`.trim();
    }
  }

  update() {
    const selected = this.selectedItems;
    const total = selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
    if (this.count) this.count.textContent = selected.length;
    if (this.total) this.total.textContent = this.formatMoney(total);
    if (this.button) this.button.disabled = selected.length === 0;
  }

  async submit() {
    const selected = this.selectedItems;
    if (!selected.length || !this.button) return;
    this.button.disabled = true;
    const originalText = this.buttonLabel?.textContent || '';

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: selected.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }))
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add the bundle');
      if (this.buttonLabel) this.buttonLabel.textContent = this.dataset.addedText || 'Bundle added';
      announce(this.dataset.addedText || 'Bundle added to cart');
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      window.setTimeout(() => {
        if (this.buttonLabel) this.buttonLabel.textContent = originalText;
      }, 1800);
    } catch (error) {
      announce(error.message);
    } finally {
      this.button.disabled = false;
    }
  }
}

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-recent-grid]');
    this.empty = this.querySelector('[data-recent-empty]');
    this.limit = Number(this.dataset.limit || 4);
    this.currentHandle = this.dataset.productHandle;
    this.load();
  }

  readHandles() {
    try {
      const value = JSON.parse(localStorage.getItem('catalog-command-recent') || '[]');
      return Array.isArray(value) ? value.filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  async load() {
    const previous = this.readHandles().filter((handle) => handle !== this.currentHandle);
    if (this.currentHandle) {
      localStorage.setItem(
        'catalog-command-recent',
        JSON.stringify([this.currentHandle, ...previous].slice(0, 12))
      );
    }

    const handles = previous.slice(0, this.limit);
    if (!handles.length) return;

    const cards = await Promise.all(handles.map(async (handle) => {
      try {
        const url = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
        const response = await fetch(url);
        if (!response.ok) return '';
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const template = parsed.querySelector('template[data-recent-card]');
        return template?.content.querySelector('[data-recent-product]')?.innerHTML || '';
      } catch {
        return '';
      }
    }));

    const validCards = cards.filter(Boolean);
    if (!validCards.length || !this.grid) return;
    this.grid.innerHTML = validCards.join('');
    this.classList.add('is-ready');
  }
}

class ShippingThreshold extends HTMLElement {
  connectedCallback() {
    this.message = this.querySelector('[data-shipping-message]');
    this.meter = this.querySelector('[data-shipping-meter]');
    this.threshold = Number(this.dataset.threshold || 0);
    this.update(Number(this.dataset.cartTotal || 0));
    document.addEventListener('cart:changed', () => this.refresh());
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }

  update(total) {
    const remaining = Math.max(0, this.threshold - total);
    if (this.message) {
      this.message.textContent = remaining === 0
        ? this.dataset.success
        : `${this.dataset.progress} ${this.formatMoney(remaining)}`;
    }
    if (this.meter) {
      const percentage = this.threshold > 0 ? Math.min(100, (total / this.threshold) * 100) : 100;
      this.meter.style.width = `${percentage}%`;
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.update(Number(cart.total_price || 0));
    } catch {
      // The server-rendered state remains available when the cart request fails.
    }
  }
}

customElements.define('theme-select', ThemeSelect);
customElements.define('menu-drawer', MenuDrawer);
customElements.define('predictive-search', PredictiveSearch);
customElements.define('quick-add', QuickAdd);
customElements.define('quantity-input', QuantityInput);
customElements.define('gift-card-recipient', GiftCardRecipient);
customElements.define('product-gallery', ProductGallery);
customElements.define('product-description', ProductDescription);
customElements.define('variant-selects', VariantSelects);
customElements.define('compare-toggle', CompareToggle);
customElements.define('product-card-variants', ProductCardVariants);
customElements.define('product-recommendations', ProductRecommendations);
customElements.define('collection-loader', CollectionLoader);
customElements.define('bundle-builder', BundleBuilder);
customElements.define('recently-viewed', RecentlyViewed);
customElements.define('shipping-threshold', ShippingThreshold);
customElements.define('selling-tools', SellingTools);

enhanceThemeSelects();
document.addEventListener('shopify:section:load', (event) => enhanceThemeSelects(event.target));

(()=>{
 let pageBusy=false;
 const updatePage=async(form)=>{
  const section=form.closest('[data-ix-cart-page]');if(!section||pageBusy||!form.reportValidity())return;
  const updates={};form.querySelectorAll('[data-ix-cart-key]').forEach(row=>updates[row.dataset.ixCartKey]=Number(row.querySelector('input[name="updates[]"]').value));
  if(Object.values(updates).some(n=>!Number.isInteger(n)||n<0))return;
  pageBusy=true;const status=section.querySelector('[data-ix-cart-status]');status.textContent=section.dataset.busy;status.dataset.error='false';section.setAttribute('aria-busy','true');const controls=[...section.querySelectorAll('button,input,textarea,select')];controls.forEach(n=>{n.dataset.wasDisabled=String(n.disabled);n.disabled=true;});
  try{const response=await fetch(section.dataset.updateUrl,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({updates,note:form.querySelector('[name=note]')?.value||'',sections:[section.dataset.sectionId],sections_url:location.pathname})});const cart=await response.json();if(!response.ok)throw new Error(cart.description||section.dataset.error);const markup=cart.sections?.[section.dataset.sectionId];if(!markup){location.reload();return;}const doc=new DOMParser().parseFromString(markup,'text/html'),fresh=doc.querySelector('[data-ix-cart-page]');if(!fresh){location.reload();return;}section.replaceWith(fresh);const newStatus=fresh.querySelector('[data-ix-cart-status]');if(newStatus){newStatus.textContent=fresh.dataset.saved;newStatus.tabIndex=-1;newStatus.focus();}document.querySelectorAll('.market-header__cart strong').forEach(n=>{n.textContent=cart.item_count;n.hidden=cart.item_count===0;});document.dispatchEvent(new CustomEvent('cart:updated',{detail:cart}));}
  catch(error){status.textContent=error.message||section.dataset.error;status.dataset.error='true';}
  finally{controls.forEach(n=>{n.disabled=n.dataset.wasDisabled==='true';delete n.dataset.wasDisabled;});section.removeAttribute('aria-busy');pageBusy=false;}
 };
 document.addEventListener('submit',async e=>{
  const form=e.target;if(form.matches('[data-ix-cart-page] .cart-layout')&&e.submitter?.name==='update'){e.preventDefault();updatePage(form);return;}
  if(!form.matches('[data-ix-companion]')||!form.closest('[data-cart-dialog]'))return;
  e.preventDefault();const selling=form.closest('selling-tools');if(form.dataset.busy||selling?.cartBusy)return;
  form.dataset.busy='true';if(selling){selling.cartBusy=true;selling.companionRequest?.abort();}
  const error=form.querySelector('[data-ix-companion-error]'),dialog=form.closest('[data-cart-dialog]');
  const controls=[...dialog.querySelectorAll('button:not([data-dialog-close]),input,select')];
  controls.forEach(n=>{n.dataset.wasDisabled=String(n.disabled);n.disabled=true;});dialog.setAttribute('aria-busy','true');error.hidden=true;
  try{const response=await fetch(form.action+'.js',{method:'POST',headers:{'Accept':'application/json','Content-Type':'application/json'},body:JSON.stringify({items:[{id:Number(form.querySelector('[name=id]').value),quantity:1}]})});const result=await response.json();if(!response.ok)throw new Error(result.description||selling?.dataset.ixError);await selling.refreshCart(false);}
  catch(err){error.textContent=err.message;error.hidden=false;}
  finally{controls.forEach(n=>{n.disabled=n.dataset.wasDisabled==='true';delete n.dataset.wasDisabled;});dialog.removeAttribute('aria-busy');delete form.dataset.busy;if(selling)selling.cartBusy=false;}
 });
 document.addEventListener('change',e=>{if(e.target.matches('[data-ix-cart-page] input[name="updates[]"]'))updatePage(e.target.form);});
 document.addEventListener('click',e=>{const link=e.target.closest('[data-ix-cart-page] .cart-item__remove');if(!link)return;e.preventDefault();if(pageBusy)return;const row=link.closest('[data-ix-cart-key]'),input=row.querySelector('[name="updates[]"]');input.value=0;updatePage(input.form);});
})();




(() => {
  const ranks = new Map();
  let addBusy = false;
  class IxCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cards = [...this.querySelectorAll('[data-rec-product]')];
      this.cards.forEach(card => { if (!ranks.has(card.dataset.recProduct)) ranks.set(card.dataset.recProduct, Math.random()); });
      this.cards.sort((a, b) => ranks.get(a.dataset.recProduct) - ranks.get(b.dataset.recProduct));
      this.offset = 0;
      this.showCards();
      const more = this.querySelector('[data-rec-more]');
      more.hidden = this.cards.length <= 3;
      more.addEventListener('click', () => {
        this.offset = (this.offset + 3) % this.cards.length;
        this.showCards();
        this.querySelector('[data-rec-status]').textContent = this.dataset.refreshed;
      });
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-rec-variant]')) return;
        const option = event.target.selectedOptions[0];
        const card = event.target.closest('[data-rec-product]');
        card.querySelector('[data-rec-price]').textContent = option.dataset.price;
        const credit = card.querySelector('[data-rec-credit]');
        if (credit) credit.textContent = option.dataset.credit;
      });
      this.addEventListener('submit', event => this.addItem(event));
    }
    showCards() {
      const list = this.querySelector('.ix-recs__list');
      this.cards.forEach(card => { card.hidden = true; });
      for (let i = 0; i < Math.min(3, this.cards.length); i++) {
        const card = this.cards[(this.offset + i) % this.cards.length];
        card.hidden = false;
        list.append(card);
      }
      list.scrollLeft = 0;
    }
    async addItem(event) {
      const form = event.target;
      if (!form.matches('[data-ix-recommendation-form]')) return;
      event.preventDefault();
      const selling = document.querySelector('selling-tools');
      const page = this.closest('[data-ix-cart-page]');
      if (addBusy || selling?.cartBusy || page?.getAttribute('aria-busy') === 'true') return;
      if (!form.reportValidity()) return;
      addBusy = true;
      const body = new FormData(form);
      const button = form.querySelector('button[type=submit]');
      const original = button.textContent;
      const status = this.querySelector('[data-rec-status]');
      const surface = this.closest('[data-cart-dialog]') || page || this;
      const controls = [...surface.querySelectorAll('button:not([data-dialog-close]),input,select,textarea')].map(node => [node, node.disabled]);
      controls.forEach(([node]) => { node.disabled = true; });
      surface.setAttribute('aria-busy', 'true');
      if (selling) { selling.cartBusy = true; selling.companionRequest?.abort(); }
      button.textContent = this.dataset.adding;
      status.textContent = this.dataset.adding;
      status.dataset.error = 'false';
      let added = false;
      try {
        const response = await fetch(form.action + '.js', {method:'POST',headers:{Accept:'application/json'},body});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.dataset.error);
        added = true;
        if (page) {
          const url = new URL(location.href);
          url.searchParams.set('section_id', page.dataset.sectionId);
          const refreshed = await fetch(url);
          if (!refreshed.ok) throw new Error('refresh');
          const doc = new DOMParser().parseFromString(await refreshed.text(), 'text/html');
          const fresh = doc.querySelector('[data-ix-cart-page]');
          if (!fresh) throw new Error('refresh');
          page.replaceWith(fresh);
          const saved = fresh.querySelector('[data-ix-cart-status]');
          saved.textContent = this.dataset.added;
          saved.tabIndex = -1;
          saved.focus({preventScroll:true});
          await selling?.refreshCart(false);
        } else {
          const cart = await selling?.refreshCart(false);
          if (!cart) throw new Error('refresh');
          const saved = selling.querySelector('[data-ix-drawer-status]');
          saved.textContent = this.dataset.added;
          saved.tabIndex = -1;
          saved.focus({preventScroll:true});
        }
      } catch (error) {
        if (added) { location.assign(form.querySelector('[name=return_to]').value); return; }
        status.textContent = error.message || this.dataset.error;
        status.dataset.error = 'true';
      } finally {
        controls.forEach(([node, disabled]) => { node.disabled = disabled; });
        surface.removeAttribute('aria-busy');
        button.textContent = original;
        addBusy = false;
        if (selling) selling.cartBusy = false;
      }
    }
  }
  customElements.define('ix-cart-recommendations', IxCartRecommendations);
})();
